import { MarketCountryCode } from '@backmarket/http-api'
import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import translations from '../Footer.translations'
import type { FooterContent } from '../FooterContent'

import { getCCPALink, getCookiePolicyLink, getFooterLink } from './FooterLinks'

export type FooterCategory = {
  title?: I18nDefinition
  content: FooterContent[]
}

export function useFooterCategories(): FooterCategory[] | null {
  const { market } = useMarketplace()

  switch (market.countryCode) {
    case MarketCountryCode.AT:
      return [
        {
          title: translations.categoryAbout,
          content: [
            getFooterLink('ABOUT_US'),
            getFooterLink('HIRE_EXTERNAL'),
            getFooterLink('STUDENTS_GOOD_DEALS'),
            {
              type: 'social-networks',
              networks: {
                instagram: 'https://www.instagram.com/backmarket_at',
                linkedin: 'https://www.linkedin.com/company/back-market',
                facebook: 'https://www.facebook.com/backmarket',
                x: 'https://x.com/back_market_de',
              },
            },
          ],
        },
        {
          title: translations.categoryHelp,
          content: [
            getFooterLink(
              'BECOME_REFURBISHER',
              'https://backmarket.at/de-at/seller/home',
            ),
            getFooterLink('BO_MERCHANT'),
            getFooterLink(
              'PAYMENT',
              'https://help.backmarket.com/hc/de-at/articles/360026643854-Welche-Zahlungsarten-gibt-es-auf-Back-Market-',
            ),
            getFooterLink(
              'SHIPPING',
              'https://help.backmarket.com/hc/de-at/articles/360026646054-In-welches-Land-kann-ich-liefern-lassen-und-wie-viel-kostet-mich-das-',
            ),
            getFooterLink(
              'RETURN',
              'https://help.backmarket.com/hc/de-at/categories/360002106194-R%C3%BCckgabe-R%C3%BCckerstattung-Returns-Refunds--Devoluciones-y-reembolsos--%CE%95%CF%80%CE%B9%CF%83%CF%84%CF%81%CE%BF%CF%86%CE%AD%CF%82-%CE%91%CF%80%CE%BF%CE%B6%CE%B7%CE%BC%CE%B9%CF%8E%CF%83%CE%B5%CE%B9%CF%82-Reso-del-prodotto-e-rimborsi-Returns-Refunds--Retours-Remboursements-Retouren-en-terugbetalingen-Returns-refunds-',
            ),
            getFooterLink('HELP'),
            getFooterLink('BUYING_GUIDES'),
            getFooterLink('MODELS_COMPARISON'),
          ],
        },
        {
          title: translations.categoryLaw,
          content: [
            getFooterLink('TERMS_USE'),
            getFooterLink('TERMS_SALE'),
            getFooterLink('BACKMARKET_WARRANTY'),
            getFooterLink('DATA_PROTECTION'),
            getCookiePolicyLink(),
            getFooterLink('INFOS_CONSUMER'),
            getFooterLink('LEGAL'),
            getFooterLink(
              'ILLEGAL_CONTENT',
              'https://help.backmarket.com/hc/de-at/requests/new?ticket_form_id=12226993197084',
            ),
            {
              key: translations.paymentMethods,
              type: 'payment-networks',
            },
          ],
        },
        {
          title: translations.categoryHello,
          content: [
            getFooterLink(
              'GLASSDOOR',
              'https://www.glassdoor.at/%C3%9Cberblick/Arbeit-bei-Back-Market-EI_IE1780208.11,22.htm?countryRedirect=true',
            ),
            getFooterLink('MEDIUM'),
          ],
        },
        {
          content: [
            {
              type: 'logos',
              logos: ['bcorp', 'customer-service-of-the-year'],
            },
          ],
        },
      ]
    case MarketCountryCode.AU:
      return [
        {
          title: translations.categoryAbout,
          content: [
            getFooterLink('ABOUT_US'),
            getFooterLink('HIRE_EXTERNAL'),
            {
              type: 'social-networks',
              networks: {
                instagram: 'https://www.instagram.com/backmarket',
                x: 'https://x.com/backmarket',
                linkedin: 'https://linkedin.com/company/back-market',
                facebook: 'https://www.facebook.com/backmarketaus',
              },
            },
          ],
        },
        {
          title: translations.categoryHelp,
          content: [
            getFooterLink(
              'BECOME_REFURBISHER',
              'https://backmarket.com.au/en-au/seller/home',
            ),
            getFooterLink('BO_MERCHANT'),
            getFooterLink(
              'PAYMENT',
              'https://help.backmarket.com/hc/en-au/articles/360026643854',
            ),
            getFooterLink(
              'SHIPPING',
              'https://help.backmarket.com/hc/en-au/articles/360033250174',
            ),
            getFooterLink(
              'RETURN',
              'https://help.backmarket.com/hc/en-au/categories/360002106194-Returns-refunds',
            ),
            getFooterLink('HELP'),
            getFooterLink('BUYING_GUIDES'),
            getFooterLink('GIFT_FINDER'),
            getFooterLink('MODELS_COMPARISON'),
          ],
        },
        {
          title: translations.categoryLaw,
          content: [
            getFooterLink('TERMS_SERVICE'),
            getFooterLink('BACKMARKET_WARRANTY'),
            getFooterLink('DATA_PROTECTION'),
            getCookiePolicyLink(),
            {
              key: translations.paymentMethods,
              type: 'payment-networks',
            },
          ],
        },
        {
          title: translations.categoryHello,
          content: [
            getFooterLink(
              'TRUST_PILOT',
              'https://fr.trustpilot.com/review/backmarket.com.au',
            ),
            getFooterLink(
              'GLASSDOOR',
              'https://www.glassdoor.com.au/Overview/Working-at-Back-Market-EI_IE1780208.11,22.htm',
            ),
            getFooterLink('MEDIUM'),
          ],
        },
        {
          content: [
            {
              type: 'logos',
              logos: ['bcorp'],
            },
          ],
        },
      ]
    case MarketCountryCode.BE:
      return [
        {
          title: translations.categoryAbout,
          content: [
            getFooterLink('ABOUT_US'),
            getFooterLink('HIRE_EXTERNAL'),
            getFooterLink('PRO', 'https://pro.backmarket.fr'),
            {
              type: 'social-networks',
              networks: {
                instagram: 'https://www.instagram.com/black_market_be',
                facebook: 'https://www.facebook.com/BackMarketCom',
                x: 'https://x.com/back_market',
              },
            },
          ],
        },
        {
          title: translations.categoryHelp,
          content: [
            getFooterLink(
              'BECOME_REFURBISHER',
              'https://backmarket.be/fr-be/seller/home',
            ),
            getFooterLink('BO_MERCHANT'),
            getFooterLink(
              'PAYMENT',
              'https://help.backmarket.com/hc/fr-be/articles/360026643854-Quels-sont-les-moyens-de-paiement-accept%C3%A9s-sur-Back-Market',
            ),
            getFooterLink(
              'SHIPPING',
              'https://help.backmarket.com/hc/fr-be/articles/360026646054-Dans-quel-pays-puis-je-me-faire-livrer-et-combien-cela-me-co%C3%BBte',
            ),
            getFooterLink(
              'RETURN',
              'https://help.backmarket.com/hc/fr-be/categories/360002106194-Retours-Remboursements-Retouren-en-terugbetalingen-Returns-refunds-',
            ),
            getFooterLink('INSURANCES'),
            getFooterLink('HELP'),
            getFooterLink('BUYING_GUIDES'),
            getFooterLink('MODELS_COMPARISON'),
          ],
        },
        {
          title: translations.categoryLaw,
          content: [
            getFooterLink('TERMS_USE'),
            getFooterLink('TERMS_SALE'),
            getFooterLink('BACKMARKET_WARRANTY'),
            getFooterLink('DATA_PROTECTION'),
            getCookiePolicyLink(),
            getFooterLink('INFOS_CONSUMER'),
            getFooterLink('LEGAL'),
            getFooterLink(
              'ILLEGAL_CONTENT',
              'https://help.backmarket.com/hc/fr-be/requests/new?ticket_form_id=12226993197084',
            ),
            {
              key: translations.paymentMethods,
              type: 'payment-networks',
            },
          ],
        },
        {
          title: translations.categoryHello,
          content: [
            getFooterLink(
              'TRUST_PILOT',
              'https://fr.trustpilot.com/review/backmarket.be',
            ),
            getFooterLink(
              'GLASSDOOR',
              'https://fr.glassdoor.be/Pr%C3%A9sentation/Travailler-chez-Back-Market-EI_IE1780208.16,27.htm',
            ),
            getFooterLink('MEDIUM'),
          ],
        },
        {
          content: [
            {
              type: 'logos',
              logos: ['bcorp'],
            },
          ],
        },
      ]
    case MarketCountryCode.DE:
      return [
        {
          title: translations.categoryAbout,
          content: [
            getFooterLink('ABOUT_US'),
            getFooterLink(
              'PRESS_CMS',
              '/de-de/about-us#scroll=false&target=heading-2-pressematerial',
            ),
            getFooterLink('HIRE_EXTERNAL'),
            getFooterLink(
              'TRUST_PILOT',
              'https://de.trustpilot.com/review/www.backmarket.de',
            ),
            {
              type: 'social-networks',
              networks: {
                instagram: 'https://www.instagram.com/backmarket_de',
                youtube: 'https://www.youtube.com/@backmarketdeutschland',
                facebook: 'https://www.facebook.com/backmarket',
                x: 'https://x.com/back_market_de',
              },
            },
          ],
        },
        {
          title: translations.categoryHelp,
          content: [
            getFooterLink(
              'CONTACT_US',
              'https://help.backmarket.com/hc/de/articles/360026654654-Wie-kontaktiere-ich-den-Kundenservice-von-Back-Market',
            ),
            getFooterLink('HELP'),
            getFooterLink(
              'SHIPPING',
              'https://help.backmarket.com/hc/de/articles/360026646054-In-welches-Land-kann-ich-liefern-lassen-und-wie-viel-kostet-mich-das',
            ),
            getFooterLink(
              'RETURN',
              'https://help.backmarket.com/hc/de/categories/360002106194-R%C3%BCckgabe-R%C3%BCckerstattung-Returns-refunds',
            ),
          ],
        },
        {
          title: translations.categoryServices,
          content: [
            getFooterLink('BACKMARKET_WARRANTY'),
            getFooterLink('INSURANCES'),
            getFooterLink('BUY_BACK'),
            getFooterLink('STUDENTS_GOOD_DEALS'),
            // getFooterLink('REFER_FRIEND'), // Not live yet
            getFooterLink(
              'BECOME_REFURBISHER',
              'https://backmarket.de/de-de/seller/home',
            ),
            getFooterLink('BO_MERCHANT'),
            {
              key: translations.paymentMethods,
              type: 'payment-networks-with-link',
              url: 'https://help.backmarket.com/hc/de/articles/360026643854-Welche-Zahlungsarten-gibt-es-auf-Back-Market',
            },
          ],
        },
        {
          title: translations.categoryGuides,
          content: [
            getFooterLink('BUYING_GUIDES'),
            getFooterLink('MODELS_COMPARISON'),
            getFooterLink('GIFT_FINDER'),
            getFooterLink('BLACK_FRIDAY'),
          ],
        },
        {
          title: translations.categoryLaw,
          content: [
            getFooterLink('TERMS_USE'),
            getFooterLink('TERMS_SALE'),
            getFooterLink('TERMS_BUYBACK'),
            getCookiePolicyLink(),
            getFooterLink('DATA_PROTECTION'),
            getFooterLink('INFOS_CONSUMER'),
            getFooterLink('LEGAL'),
            getFooterLink(
              'ILLEGAL_CONTENT',
              'https://help.backmarket.com/hc/de/requests/new?ticket_form_id=12226993197084',
            ),
          ],
        },
        {
          content: [
            {
              type: 'logos',
              logos: ['bcorp'],
            },
          ],
        },
      ]
    case MarketCountryCode.ES:
      return [
        {
          title: translations.categoryAbout,
          content: [
            getFooterLink('ABOUT_US'),
            getFooterLink(
              'PRESS_CMS',
              '/es-es/about-us#scroll=false&target=heading-2-kit-para-medios',
            ),
            getFooterLink('HIRE_EXTERNAL'),
            getFooterLink(
              'TRUST_PILOT',
              'https://es.trustpilot.com/review/www.backmarket.es',
            ),
            {
              type: 'social-networks',
              networks: {
                instagram: 'https://www.instagram.com/backmarket_es',
                linkedin: 'https://www.linkedin.com/company/back-market',
                facebook: 'https://www.facebook.com/backmarket.es',
                x: 'https://x.com/back_market_es',
              },
            },
          ],
        },
        {
          title: translations.categoryHelp,
          content: [
            getFooterLink(
              'CONTACT_US',
              'https://help.backmarket.com/hc/es/articles/360026654654--C%C3%B3mo-contacto-con-la-Atenci%C3%B3n-al-cliente-de-Back-Market',
            ),
            getFooterLink('HELP'),
            getFooterLink(
              'SHIPPING',
              'https://help.backmarket.com/hc/es/articles/360026646054--En-qu%C3%A9-pa%C3%ADs-me-pueden-entregar-y-cu%C3%A1nto-me-cuesta',
            ),
            getFooterLink(
              'RETURN',
              'https://help.backmarket.com/hc/es/categories/360002106194-Devoluciones-y-reembolsos--%CE%95%CF%80%CE%B9%CF%83%CF%84%CF%81%CE%BF%CF%86%CE%AD%CF%82-%CE%91%CF%80%CE%BF%CE%B6%CE%B7%CE%BC%CE%B9%CF%8E%CF%83%CE%B5%CE%B9%CF%82-Reso-del-prodotto-e-rimborsi-Returns-Refunds--Retours-Remboursements-Retouren-en-terugbetalingen-Returns-refunds-',
            ),
          ],
        },
        {
          title: translations.categoryServices,
          content: [
            getFooterLink('BACKMARKET_WARRANTY'),
            getFooterLink('INSURANCES'),
            getFooterLink('BUY_BACK'),
            getFooterLink('STUDENTS_GOOD_DEALS'),
            // getFooterLink('REFER_FRIEND'), // Not live yet
            getFooterLink(
              'BECOME_REFURBISHER',
              'https://backmarket.es/es-es/seller/home',
            ),
            getFooterLink('BO_MERCHANT'),
            {
              key: translations.paymentMethods,
              type: 'payment-networks-with-link',
              url: 'https://help.backmarket.com/hc/es/articles/360026643854--Qu%C3%A9-m%C3%A9todos-de-pago-se-aceptan-en-Back-Market',
            },
          ],
        },
        {
          title: translations.categoryGuides,
          content: [
            getFooterLink('BUYING_GUIDES'),
            getFooterLink('MODELS_COMPARISON'),
            getFooterLink('GIFT_FINDER'),
            getFooterLink('BLACK_FRIDAY'),
          ],
        },
        {
          title: translations.categoryLaw,
          content: [
            getFooterLink('TERMS_USE'),
            getFooterLink('TERMS_SALE'),
            getFooterLink('TERMS_BUYBACK'),
            getCookiePolicyLink(),
            getFooterLink('DATA_PROTECTION'),
            getFooterLink('INFOS_CONSUMER'),
            getFooterLink('LEGAL'),
            getFooterLink(
              'ILLEGAL_CONTENT',
              'https://help.backmarket.com/hc/es/requests/new?ticket_form_id=12226993197084',
            ),
          ],
        },
        {
          content: [
            {
              type: 'logos',
              logos: ['bcorp'],
            },
          ],
        },
      ]
    case MarketCountryCode.FI:
      return [
        {
          title: translations.categoryAbout,
          content: [
            getFooterLink('ABOUT_US'),
            getFooterLink('STUDENTS_GOOD_DEALS'),
            getFooterLink('HIRE_EXTERNAL'),
            {
              type: 'social-networks',
              networks: {
                linkedin: 'https://www.linkedin.com/company/back-market',
                facebook: 'https://www.facebook.com/backmarket',
                x: 'https://x.com/backmarket',
              },
            },
          ],
        },
        {
          title: translations.categoryHelp,
          content: [
            getFooterLink(
              'BECOME_REFURBISHER',
              'https://backmarket.fi/fi-fi/seller/home',
            ),
            getFooterLink('BO_MERCHANT'),
            getFooterLink(
              'PAYMENT',
              'https://help.backmarket.com/hc/fi/articles/360026643854',
            ),
            getFooterLink(
              'SHIPPING',
              'https://help.backmarket.com/hc/fi/articles/360026646054',
            ),
            getFooterLink(
              'RETURN',
              'https://help.backmarket.com/hc/fi/categories/360002106194-Palautukset-ja-hyvitykset-Vr%C3%A1tenie-objedn%C3%A1vky-a-pe%C5%88az%C3%AD-%CE%95%CF%80%CE%B9%CF%83%CF%84%CF%81%CE%BF%CF%86%CE%AD%CF%82-%CE%91%CF%80%CE%BF%CE%B6%CE%B7%CE%BC%CE%B9%CF%8E%CF%83%CE%B5%CE%B9%CF%82-Reso-del-prodotto-e-rimborsi-Returns-Refunds--Retours-Remboursements-Retouren-en-terugbetalingen-Returns-refunds-',
            ),
            getFooterLink('HELP'),
            getFooterLink('BUYING_GUIDES'),
            getFooterLink('MODELS_COMPARISON'),
          ],
        },
        {
          title: translations.categoryLaw,
          content: [
            getFooterLink('TERMS_USE'),
            getFooterLink('TERMS_SALE'),
            getFooterLink('DATA_PROTECTION'),
            getCookiePolicyLink(),
            getFooterLink('INFOS_CONSUMER'),
            getFooterLink('LEGAL'),
            getFooterLink(
              'ILLEGAL_CONTENT',
              'https://help.backmarket.com/hc/fi/requests/new?ticket_form_id=12226993197084',
            ),
            {
              key: translations.paymentMethods,
              type: 'payment-networks',
            },
          ],
        },
        {
          title: translations.categoryHello,
          content: [
            getFooterLink(
              'TRUST_PILOT',
              'https://fi.trustpilot.com/review/backmarket.fi?languages=all',
            ),
            getFooterLink(
              'GLASSDOOR',
              'https://www.glassdoor.com/Reviews/Back-Market-Reviews-E1780208_P3.html',
            ),
            getFooterLink('MEDIUM'),
          ],
        },
        {
          content: [
            {
              type: 'logos',
              logos: ['bcorp'],
            },
          ],
        },
      ]
    case MarketCountryCode.FR:
      return [
        {
          title: translations.categoryAbout,
          content: [
            getFooterLink('ABOUT_US'),
            getFooterLink(
              'PRESS_CMS',
              '/fr-fr/about-us#scroll=false&target=heading-2-kit-média',
            ),
            getFooterLink(
              'IMPACT_CMS',
              "/fr-fr/about-us#scroll=false&target=heading-2-rapport-d'impact",
            ),
            getFooterLink('ACCESSIBILITY'),
            getFooterLink(
              'TRUST_PILOT',
              'https://fr.trustpilot.com/review/www.backmarket.fr',
            ),
            getFooterLink('HIRE_EXTERNAL'),
            {
              type: 'social-networks',
              networks: {
                instagram: 'https://www.instagram.com/backmarket',
                youtube:
                  'https://www.youtube.com/channel/UCXJ_vW2t0W5hY9hS8ePnpzw',
                facebook: 'https://www.facebook.com/BackMarketCom',
                x: 'https://x.com/back_market',
              },
            },
          ],
        },
        {
          title: translations.categoryHelp,
          content: [
            getFooterLink(
              'CONTACT_US',
              'https://help.backmarket.com/hc/fr-fr/articles/360026654654-Comment-puis-je-contacter-le-service-client%C3%A8le-de-Back-Market',
            ),
            getFooterLink('HELP'),
            getFooterLink(
              'SHIPPING',
              'https://help.backmarket.com/hc/fr-fr/articles/360026646054-Dans-quel-pays-puis-je-me-faire-livrer-et-combien-cela-me-co%C3%BBte',
            ),
            getFooterLink(
              'RETURN',
              'https://help.backmarket.com/hc/fr-fr/categories/360002106194-Retours-Remboursements',
            ),
          ],
        },
        {
          title: translations.categoryServices,
          content: [
            getFooterLink('BACKMARKET_WARRANTY'),
            getFooterLink('INSURANCES'),
            getFooterLink('BUY_BACK'),
            getFooterLink('STUDENTS_GOOD_DEALS'),
            // getFooterLink('REFER_FRIEND'), // Not live yet
            getFooterLink(
              'BECOME_REFURBISHER',
              'https://backmarket.fr/fr-fr/seller/home',
            ),
            getFooterLink('BO_MERCHANT'),
            getFooterLink('PRO', 'https://pro.backmarket.fr'),
            {
              key: translations.paymentMethods,
              type: 'payment-networks-with-link',
              url: 'https://help.backmarket.com/hc/fr-fr/articles/360026643854-Quels-sont-les-moyens-de-paiement-accept%C3%A9s-sur-Back-Market',
            },
          ],
        },
        {
          title: translations.categoryGuides,
          content: [
            getFooterLink('BUYING_GUIDES'),
            getFooterLink('MODELS_COMPARISON'),
            getFooterLink('GIFT_FINDER'),
            getFooterLink('BLACK_FRIDAY'),
          ],
        },
        {
          title: translations.categoryLaw,
          content: [
            getFooterLink('TERMS_USE'),
            getFooterLink('TERMS_SALE'),
            getFooterLink('TERMS_BUYBACK'),
            getCookiePolicyLink(),
            getFooterLink('DATA_PROTECTION'),
            getFooterLink('INFOS_CONSUMER'),
            getFooterLink('LEGAL'),
            getFooterLink(
              'ILLEGAL_CONTENT',
              'https://help.backmarket.com/hc/fr-fr/requests/new?ticket_form_id=12226993197084',
            ),
          ],
        },
        {
          content: [
            {
              type: 'logos',
              logos: ['bcorp', 'best-e-commerce-website'],
            },
          ],
        },
      ]
    case MarketCountryCode.GB:
      return [
        {
          title: translations.categoryAbout,
          content: [
            getFooterLink('ABOUT_US'),
            getFooterLink(
              'PRESS_CMS',
              '/en-gb/about-us#scroll=false&target=heading-2-press-kit',
            ),
            getFooterLink(
              'IMPACT_CMS',
              '/en-gb/about-us#scroll=false&target=heading-2-our-impact-report',
            ),
            getFooterLink('HIRE_EXTERNAL'),
            getFooterLink(
              'TRUST_PILOT',
              'https://www.trustpilot.com/review/www.backmarket.co.uk',
            ),
            {
              type: 'social-networks',
              networks: {
                instagram: 'https://www.instagram.com/backmarket_uk',
                youtube:
                  'https://www.youtube.com/channel/UCeyWJxApnbMPhzOjAftCGeg',
                facebook: 'https://www.facebook.com/backmarket',
                x: 'https://x.com/backmarket',
              },
            },
          ],
        },
        {
          title: translations.categoryHelp,
          content: [
            getFooterLink(
              'CONTACT_US',
              'https://help.backmarket.com/hc/en-gb/articles/360026654654-How-do-I-contact-Back-Market-Customer-Care',
            ),
            getFooterLink('HELP'),
            getFooterLink(
              'SHIPPING',
              'https://help.backmarket.com/hc/en-gb/articles/360026646054-Where-can-I-get-delivered-and-how-much-does-it-cost-me',
            ),
            getFooterLink(
              'RETURN',
              'https://help.backmarket.com/hc/en-gb/categories/360002106194-Returns-refunds--Vr%C3%A1tenie-objedn%C3%A1vky-a-pe%C5%88az%C3%AD-%CE%95%CF%80%CE%B9%CF%83%CF%84%CF%81%CE%BF%CF%86%CE%AD%CF%82-%CE%91%CF%80%CE%BF%CE%B6%CE%B7%CE%BC%CE%B9%CF%8E%CF%83%CE%B5%CE%B9%CF%82-Reso-del-prodotto-e-rimborsi-Returns-Refunds--Retours-Remboursements-Retouren-en-terugbetalingen-Returns-refunds-',
            ),
          ],
        },
        {
          title: translations.categoryServices,
          content: [
            getFooterLink('BACKMARKET_WARRANTY'),
            getFooterLink('INSURANCES'),
            getFooterLink('BUY_BACK'),
            getFooterLink('STUDENTS_GOOD_DEALS'),
            // getFooterLink('REFER_FRIEND'), // Not live yet
            getFooterLink(
              'BECOME_REFURBISHER',
              'https://backmarket.co.uk/en-gb/seller/home',
            ),
            getFooterLink('BO_MERCHANT'),
            {
              key: translations.paymentMethods,
              type: 'payment-networks-with-link',
              url: 'https://help.backmarket.com/hc/en-gb/articles/360026643854-What-payment-methods-are-accepted-on-Back-Market',
            },
          ],
        },
        {
          title: translations.categoryGuides,
          content: [
            getFooterLink('BUYING_GUIDES'),
            getFooterLink('MODELS_COMPARISON'),
            getFooterLink('GIFT_FINDER'),
            getFooterLink('BLACK_FRIDAY'),
          ],
        },
        {
          title: translations.categoryLaw,
          content: [
            getFooterLink('TERMS_USE'),
            getFooterLink('TERMS_SALE'),
            getFooterLink('TERMS_BUYBACK'),
            getCookiePolicyLink(),
            getFooterLink('DATA_PROTECTION'),
            getFooterLink('INFOS_CONSUMER'),
            getFooterLink('LEGAL'),
            getFooterLink(
              'ILLEGAL_CONTENT',
              'https://help.backmarket.com/hc/en-gb/requests/new?ticket_form_id=12226993197084',
            ),
          ],
        },
        {
          content: [
            {
              type: 'logos',
              logos: ['bcorp'],
            },
          ],
        },
      ]
    case MarketCountryCode.GR:
      return [
        {
          title: translations.categoryAbout,
          content: [
            getFooterLink('ABOUT_US'),
            getFooterLink('STUDENTS_GOOD_DEALS'),
            getFooterLink('HIRE_EXTERNAL'),
            {
              type: 'social-networks',
              networks: {
                instagram: 'https://www.instagram.com/backmarket_gr',
                facebook: 'https://www.facebook.com/backmarket',
                x: 'https://x.com/backmarket',
              },
            },
          ],
        },
        {
          title: translations.categoryHelp,
          content: [
            getFooterLink(
              'BECOME_REFURBISHER',
              'https://backmarket.gr/el-gr/seller/home',
            ),
            getFooterLink('BO_MERCHANT'),
            getFooterLink(
              'PAYMENT',
              'https://help.backmarket.com/hc/el/articles/360026643854',
            ),
            getFooterLink(
              'SHIPPING',
              'https://help.backmarket.com/hc/el/articles/360034275533',
            ),
            getFooterLink(
              'RETURN',
              'https://help.backmarket.com/hc/el/categories/360002106194-%CE%95%CF%80%CE%B9%CF%83%CF%84%CF%81%CE%BF%CF%86%CE%AD%CF%82-%CE%91%CF%80%CE%BF%CE%B6%CE%B7%CE%BC%CE%B9%CF%8E%CF%83%CE%B5%CE%B9%CF%82-Reso-del-prodotto-e-rimborsi-Returns-Refunds--Retours-Remboursements-Retouren-en-terugbetalingen-Returns-refunds-',
            ),
            getFooterLink('HELP'),
            getFooterLink('BUYING_GUIDES'),
            getFooterLink('MODELS_COMPARISON'),
          ],
        },
        {
          title: translations.categoryLaw,
          content: [
            getFooterLink('TERMS_USE'),
            getFooterLink('TERMS_SALE'),
            getFooterLink('DATA_PROTECTION'),
            getCookiePolicyLink(),
            getFooterLink('INFOS_CONSUMER'),
            getFooterLink('LEGAL'),
            getFooterLink(
              'ILLEGAL_CONTENT',
              'https://help.backmarket.com/hc/el-gr/requests/new?ticket_form_id=12226993197084',
            ),
            {
              key: translations.paymentMethods,
              type: 'payment-networks',
            },
          ],
        },
        {
          title: translations.categoryHello,
          content: [
            getFooterLink(
              'TRUST_PILOT',
              'https://gr.trustpilot.com/review/backmarket.gr',
            ),
          ],
        },
        {
          content: [
            {
              type: 'logos',
              logos: ['bcorp'],
            },
          ],
        },
      ]
    case MarketCountryCode.IE:
      return [
        {
          title: translations.categoryAbout,
          content: [
            getFooterLink('ABOUT_US'),
            getFooterLink('STUDENTS_GOOD_DEALS'),
            getFooterLink('HIRE_EXTERNAL'),
            {
              type: 'social-networks',
              networks: {
                instagram: 'https://www.instagram.com/backmarket_ie',
                linkedin: 'https://www.linkedin.com/company/back-market',
                facebook: 'https://www.facebook.com/backmarket',
                x: 'https://x.com/backmarket',
              },
            },
          ],
        },
        {
          title: translations.categoryHelp,
          content: [
            getFooterLink(
              'BECOME_REFURBISHER',
              'https://backmarket.ie/en-ie/seller/home',
            ),
            getFooterLink('BO_MERCHANT'),
            getFooterLink(
              'PAYMENT',
              'https://help.backmarket.com/hc/en-ie/articles/360026643854-What-payment-methods-are-accepted-on-Back-Market-',
            ),
            getFooterLink(
              'SHIPPING',
              'https://help.backmarket.com/hc/en-ie/articles/360026646054-Where-can-I-get-delivered-and-how-much-does-it-cost-me-',
            ),
            getFooterLink(
              'RETURN',
              'https://help.backmarket.com/hc/en-ie/categories/360002106194-Returns-refunds-R%C3%BCckgabe-R%C3%BCckerstattung-Returns-refunds',
            ),
            getFooterLink('HELP'),
            getFooterLink('BUYING_GUIDES'),
            getFooterLink('MODELS_COMPARISON'),
          ],
        },
        {
          title: translations.categoryLaw,
          content: [
            getFooterLink('TERMS_USE'),
            getFooterLink('TERMS_SALE'),
            getFooterLink('BACKMARKET_WARRANTY'),
            getFooterLink('DATA_PROTECTION'),
            getCookiePolicyLink(),
            getFooterLink('INFOS_CONSUMER'),
            getFooterLink('LEGAL'),
            getFooterLink(
              'ILLEGAL_CONTENT',
              'https://help.backmarket.com/hc/en-ie/requests/new?ticket_form_id=12226993197084',
            ),
            {
              key: translations.paymentMethods,
              type: 'payment-networks',
            },
          ],
        },
        {
          title: translations.categoryHello,
          content: [
            // TODO: Missing link for trust pilot: getFooterLink('TRUST_PILOT', )
            getFooterLink('MEDIUM'),
          ],
        },
        {
          content: [
            {
              type: 'logos',
              logos: ['bcorp'],
            },
          ],
        },
      ]
    case MarketCountryCode.IT:
      return [
        {
          title: translations.categoryAbout,
          content: [
            getFooterLink('ABOUT_US'),
            getFooterLink('STUDENTS_GOOD_DEALS'),
            getFooterLink('HIRE_EXTERNAL'),
            {
              type: 'social-networks',
              networks: {
                instagram: 'https://www.instagram.com/backmarket_it',
                linkedin: 'https://www.linkedin.com/company/back-market',
                facebook: 'https://www.facebook.com/backmarket',
                x: 'https://x.com/backmarket_it',
              },
            },
          ],
        },
        {
          title: translations.categoryHelp,
          content: [
            getFooterLink(
              'BECOME_REFURBISHER',
              'https://backmarket.it/it-it/seller/home',
            ),
            getFooterLink('BO_MERCHANT'),
            getFooterLink(
              'PAYMENT',
              'https://help.backmarket.com/hc/it/articles/360026643854-Quali-sono-i-metodi-di-pagamento-accettati-da-Back-Market',
            ),
            getFooterLink(
              'SHIPPING',
              'https://help.backmarket.com/hc/it/articles/360026646054-In-quale-paese-posso-essere-consegnato-e-quanto-costa',
            ),
            getFooterLink(
              'RETURN',
              'https://help.backmarket.com/hc/it/categories/360002106194-Reso-del-prodotto-e-rimborsi-Returns-Refunds--Retours-Remboursements-Retouren-en-terugbetalingen-Returns-refunds-',
            ),
            getFooterLink('INSURANCES'),
            getFooterLink('HELP'),
            getFooterLink('BUYING_GUIDES'),
            getFooterLink('MODELS_COMPARISON'),
          ],
        },
        {
          title: translations.categoryLaw,
          content: [
            getFooterLink('TERMS_USE'),
            getFooterLink('TERMS_SALE'),
            getFooterLink('BACKMARKET_WARRANTY'),
            getFooterLink('DATA_PROTECTION'),
            getCookiePolicyLink(),
            getFooterLink('INFOS_CONSUMER'),
            getFooterLink('LEGAL'),
            getFooterLink(
              'ILLEGAL_CONTENT',
              'https://help.backmarket.com/hc/it/requests/new?ticket_form_id=12226993197084',
            ),
            {
              key: translations.paymentMethods,
              type: 'payment-networks',
            },
          ],
        },
        {
          title: translations.categoryHello,
          content: [
            getFooterLink(
              'TRUSTED_SHOPS',
              'https://it.trustpilot.com/review/backmarket.it',
            ),
            getFooterLink(
              'GLASSDOOR',
              'https://www.glassdoor.it/Panoramica/Lavorando-in-Back-Market-EI_IE1780208.13,24.htm',
            ),
            getFooterLink('MEDIUM'),
          ],
        },
        {
          content: [
            {
              type: 'logos',
              logos: ['bcorp'],
            },
          ],
        },
      ]
    case MarketCountryCode.JP:
      return [
        {
          title: translations.categoryAbout,
          content: [
            getFooterLink('ABOUT_US'),
            getFooterLink(
              'IMPACT_CMS',
              '/ja-jp/about-us#scroll=false&target=heading-2-back-marketのインパクト・レポート',
            ),

            getFooterLink(
              'PRESS_CMS',
              '/ja-jp/about-us#scroll=false&target=heading-2-プレスキット',
            ),
            getFooterLink('HIRE_EXTERNAL'),

            {
              type: 'social-networks',
              networks: {
                instagram: 'https://www.instagram.com/backmarket_jp',
                youtube: 'https://www.youtube.com/@backmarket_jp',
                facebook: 'https://www.facebook.com/backmarketjapan',
                x: 'https://x.com/Backmarket_JP',
              },
            },
          ],
        },
        {
          title: translations.categoryHelp,
          content: [
            getFooterLink('HELP'),
            getFooterLink(
              'CONTACT_US',
              'https://help.backmarket.com/hc/ja/articles/360026654654-Back-Marketカスタマーケアに連絡するには',
            ),
            getFooterLink(
              'SHIPPING',
              'https://help.backmarket.com/hc/ja/articles/360033250174',
            ),
            getFooterLink(
              'RETURN',
              'https://help.backmarket.com/hc/ja/categories/360002106194-%E8%BF%94%E5%93%81-%E8%BF%94%E9%87%91%E3%81%AB%E3%81%A4%E3%81%84%E3%81%A6',
            ),
            getFooterLink('BUYING_GUIDES'),
            getFooterLink('GIFT_FINDER'),
            getFooterLink('MODELS_COMPARISON'),
            getFooterLink(
              'PAYMENT',
              'https://help.backmarket.com/hc/ja/articles/360026643854',
            ),
            {
              key: translations.paymentMethods,
              type: 'payment-networks',
            },
          ],
        },
        {
          title: translations.categoryServices,
          content: [
            getFooterLink('BACKMARKET_WARRANTY'),
            getFooterLink('INSURANCES'),
            getFooterLink('BUY_BACK'),
            getFooterLink('STUDENTS_GOOD_DEALS'),
            // getFooterLink('REFER_FRIEND'), // Not live yet
            getFooterLink(
              'BECOME_REFURBISHER',
              'https://backmarket.co.jp/ja-jp/seller/home',
            ),
            getFooterLink('BO_MERCHANT'),
          ],
        },
        {
          title: translations.categoryLaw,
          content: [
            getFooterLink('TERMS_USE'),
            getFooterLink('DATA_PROTECTION'),
            getCookiePolicyLink(),
            getFooterLink('LEGAL'),
          ],
        },
        {
          content: [
            {
              type: 'logos',
              logos: ['bcorp'],
            },
          ],
        },
      ]
    case MarketCountryCode.NL:
      return [
        {
          title: translations.categoryAbout,
          content: [
            getFooterLink('ABOUT_US'),
            getFooterLink('STUDENTS_GOOD_DEALS'),
            getFooterLink('ACCESSIBILITY'),
            getFooterLink('HIRE_EXTERNAL'),
            {
              type: 'social-networks',
              networks: {
                instagram: 'https://www.instagram.com/backmarket_nl',
                linkedin: 'https://www.linkedin.com/company/back-market',
                facebook: 'https://www.facebook.com/backmarket',
                x: 'https://x.com/backmarket',
              },
            },
          ],
        },
        {
          title: translations.categoryHelp,
          content: [
            getFooterLink(
              'BECOME_REFURBISHER',
              'https://backmarket.nl/nl-nl/seller/home',
            ),
            getFooterLink('BO_MERCHANT'),
            getFooterLink(
              'PAYMENT',
              'https://help.backmarket.com/hc/nl/articles/360026643854-Hoe-kan-ik-op-Back-Market-betalen-',
            ),
            getFooterLink(
              'SHIPPING',
              'https://help.backmarket.com/hc/nl/articles/360033250174-Wanneer-wordt-mijn-bestelling-bezorgd-',
            ),
            getFooterLink(
              'RETURN',
              'https://help.backmarket.com/hc/nl/categories/360002106194-Retouren-en-terugbetalingen-Returns-refunds-',
            ),
            getFooterLink('INSURANCES'),
            getFooterLink('HELP'),
            getFooterLink('BUYING_GUIDES'),
            getFooterLink('MODELS_COMPARISON'),
          ],
        },
        {
          title: translations.categoryLaw,
          content: [
            getFooterLink('TERMS_USE'),
            getFooterLink('TERMS_SALE'),
            getFooterLink('BACKMARKET_WARRANTY'),
            getFooterLink('DATA_PROTECTION'),
            getCookiePolicyLink(),
            getFooterLink('INFOS_CONSUMER'),
            getFooterLink('LEGAL'),
            getFooterLink(
              'ILLEGAL_CONTENT',
              'https://help.backmarket.com/hc/nl/requests/new?ticket_form_id=12226993197084',
            ),
            {
              key: translations.paymentMethods,
              type: 'payment-networks',
            },
          ],
        },
        {
          title: translations.categoryHello,
          content: [
            getFooterLink(
              'TRUST_PILOT',
              'https://www.trustpilot.com/review/www.backmarket.nl',
            ),
            getFooterLink(
              'GLASSDOOR',
              'https://www.glassdoor.nl/Overzicht/Werken-bij-Back-Market-EI_IE1780208.11,22.htm',
            ),
            getFooterLink('MEDIUM'),
          ],
        },
        {
          content: [
            {
              type: 'logos',
              logos: ['bcorp'],
            },
          ],
        },
      ]
    case MarketCountryCode.PT:
      return [
        {
          title: translations.categoryAbout,
          content: [
            getFooterLink('ABOUT_US'),
            getFooterLink('STUDENTS_GOOD_DEALS'),
            getFooterLink('HIRE_EXTERNAL'),
            {
              type: 'social-networks',
              networks: {
                instagram: 'https://www.instagram.com/backmarket_pt',
                linkedin: 'https://www.linkedin.com/company/back-market',
                facebook: 'https://www.facebook.com/backmarket.es',
              },
            },
          ],
        },
        {
          title: translations.categoryHelp,
          content: [
            getFooterLink(
              'BECOME_REFURBISHER',
              'https://backmarket.pt/pt-pt/seller/home',
            ),
            getFooterLink('BO_MERCHANT'),
            getFooterLink(
              'PAYMENT',
              'https://help.backmarket.com/hc/pt/articles/360026643854',
            ),
            getFooterLink(
              'SHIPPING',
              'https://help.backmarket.com/hc/pt/articles/360026646054',
            ),
            getFooterLink(
              'RETURN',
              'https://help.backmarket.com/hc/pt/categories/360002106194-Devolu%C3%A7%C3%B5es-Reembolsos-Returns-refunds-R%C3%BCckgabe-R%C3%BCckerstattung-Returns-refunds',
            ),
            getFooterLink('HELP'),
            getFooterLink('BUYING_GUIDES'),
            getFooterLink('MODELS_COMPARISON'),
          ],
        },
        {
          title: translations.categoryLaw,
          content: [
            getFooterLink('TERMS_USE'),
            getFooterLink('TERMS_SALE'),
            getFooterLink('BACKMARKET_WARRANTY'),
            getFooterLink('DATA_PROTECTION'),
            getCookiePolicyLink(),
            getFooterLink('INFOS_CONSUMER'),
            getFooterLink('LEGAL'),
            getFooterLink(
              'ILLEGAL_CONTENT',
              'https://help.backmarket.com/hc/pt/requests/new?ticket_form_id=12226993197084',
            ),
            {
              key: translations.paymentMethods,
              type: 'payment-networks',
            },
          ],
        },
        {
          title: translations.categoryHello,
          content: [
            getFooterLink(
              'TRUST_PILOT',
              'https://pt.trustpilot.com/review/backmarket.pt?languages=all',
            ),
            getFooterLink('MEDIUM'),
          ],
        },
        {
          content: [
            {
              type: 'logos',
              logos: ['bcorp'],
            },
          ],
        },
      ]
    case MarketCountryCode.SE:
      return [
        {
          title: translations.categoryAbout,
          content: [
            getFooterLink('ABOUT_US'),
            getFooterLink('STUDENTS_GOOD_DEALS'),
            getFooterLink('HIRE_EXTERNAL'),
            {
              type: 'social-networks',
              networks: {
                linkedin: 'https://www.linkedin.com/company/back-market',
                facebook: 'https://www.facebook.com/backmarket',
                x: 'https://x.com/backmarket',
              },
            },
          ],
        },
        {
          title: translations.categoryHelp,
          content: [
            getFooterLink(
              'BECOME_REFURBISHER',
              'https://backmarket.se/sv-se/seller/home ',
            ),
            getFooterLink('BO_MERCHANT'),
            getFooterLink(
              'PAYMENT',
              'https://help.backmarket.com/hc/sv/articles/360026643854',
            ),
            getFooterLink(
              'SHIPPING',
              'https://help.backmarket.com/hc/sv/articles/360026646054-Vilka-l%C3%A4nder-levererar-Back-Market-till-och-vilka-%C3%A4r-de-olika-fraktalternativen',
            ),
            getFooterLink(
              'RETURN',
              'https://help.backmarket.com/hc/sv/categories/360002106194-Returns-Refunds--Devoluciones-y-reembolsos--%CE%95%CF%80%CE%B9%CF%83%CF%84%CF%81%CE%BF%CF%86%CE%AD%CF%82-%CE%91%CF%80%CE%BF%CE%B6%CE%B7%CE%BC%CE%B9%CF%8E%CF%83%CE%B5%CE%B9%CF%82-Reso-del-prodotto-e-rimborsi-Returns-Refunds--Retours-Remboursements-Retouren-en-terugbetalingen-Returns-refunds-',
            ),
            getFooterLink('HELP'),
            getFooterLink('BUYING_GUIDES'),
            getFooterLink('MODELS_COMPARISON'),
          ],
        },
        {
          title: translations.categoryLaw,
          content: [
            getFooterLink('TERMS_USE'),
            getFooterLink('TERMS_SALE'),
            getFooterLink('DATA_PROTECTION'),
            getCookiePolicyLink(),
            getFooterLink('INFOS_CONSUMER'),
            getFooterLink('LEGAL'),
            getFooterLink(
              'ILLEGAL_CONTENT',
              'https://help.backmarket.com/hc/sv/requests/new?ticket_form_id=12226993197084',
            ),
            {
              key: translations.paymentMethods,
              type: 'payment-networks-with-link',
              url: 'https://help.backmarket.com/hc/sv/articles/360026643854-Vilka-betalningsmetoder-accepteras-p%C3%A5-Back-Market',
            },
          ],
        },
        {
          title: translations.categoryHello,
          content: [
            getFooterLink(
              'TRUST_PILOT',
              'https://www.trustpilot.com/review/backmarket.se',
            ),
            getFooterLink('MEDIUM'),
          ],
        },
        {
          content: [
            {
              type: 'logos',
              logos: ['bcorp'],
            },
          ],
        },
      ]
    case MarketCountryCode.SK:
      return [
        {
          title: translations.categoryAbout,
          content: [
            getFooterLink('ABOUT_US'),
            getFooterLink('STUDENTS_GOOD_DEALS'),
            getFooterLink('HIRE_EXTERNAL'),
            {
              type: 'social-networks',
              networks: {
                linkedin: 'https://www.linkedin.com/company/back-market',
                facebook: 'https://www.facebook.com/backmarket',
                x: 'https://x.com/backmarket',
              },
            },
          ],
        },
        {
          title: translations.categoryHelp,
          content: [
            getFooterLink(
              'BECOME_REFURBISHER',
              'https://backmarket.sk/sk-sk/seller/home',
            ),
            getFooterLink('BO_MERCHANT'),
            getFooterLink(
              'PAYMENT',
              'https://help.backmarket.com/hc/sk/articles/360026643854',
            ),
            getFooterLink(
              'SHIPPING',
              'https://help.backmarket.com/hc/sk/articles/360026646054',
            ),
            getFooterLink(
              'RETURN',
              'https://help.backmarket.com/hc/sk/categories/360002106194-Vr%C3%A1tenie-objedn%C3%A1vky-a-pe%C5%88az%C3%AD-Returns-Refunds--Retours-Remboursements-Retouren-en-terugbetalingen-Returns-refunds-',
            ),
            getFooterLink('HELP'),
            getFooterLink('BUYING_GUIDES'),
            getFooterLink('MODELS_COMPARISON'),
          ],
        },
        {
          title: translations.categoryLaw,
          content: [
            getFooterLink('TERMS_USE'),
            getFooterLink('TERMS_SALE'),
            getFooterLink('DATA_PROTECTION'),
            getCookiePolicyLink(),
            getFooterLink('INFOS_CONSUMER'),
            getFooterLink('LEGAL'),
            getFooterLink(
              'ILLEGAL_CONTENT',
              'https://help.backmarket.com/hc/sk/requests/new?ticket_form_id=12226993197084',
            ),
            {
              key: translations.paymentMethods,
              type: 'payment-networks',
            },
          ],
        },
        {
          title: translations.categoryHello,
          content: [getFooterLink('MEDIUM')],
        },
        {
          content: [
            {
              type: 'logos',
              logos: ['bcorp'],
            },
          ],
        },
      ]
    case MarketCountryCode.US:
      return [
        {
          title: translations.categoryAbout,
          content: [
            getFooterLink('ABOUT_US'),
            getFooterLink(
              'PRESS_CMS',
              '/en-us/about-us#scroll=false&target=heading-2-press-kit',
            ),
            getFooterLink(
              'IMPACT_CMS',
              '/en-us/about-us#scroll=false&target=heading-2-our-impact-report',
            ),
            getFooterLink('STORE'),
            getFooterLink('ACCESSIBILITY'),
            getFooterLink('HIRE_INTERNAL'),
            getFooterLink(
              'TRUST_PILOT',
              'https://www.trustpilot.com/review/www.backmarket.com',
            ),
            {
              type: 'social-networks',
              networks: {
                instagram: 'https://www.instagram.com/backmarket',
                tiktok: 'https://www.tiktok.com/@backmarket',
                youtube: 'https://www.youtube.com/@backmarketusa',
                linkedin: 'https://www.linkedin.com/company/back-market',
              },
            },
          ],
        },
        {
          title: translations.categoryHelp,
          content: [
            getFooterLink(
              'CONTACT_US',
              'https://help.backmarket.com/hc/en-us/articles/360026654654-How-do-I-contact-Back-Market-Customer-Care',
            ),
            getFooterLink('HELP'),
            getFooterLink(
              'SHIPPING',
              'https://help.backmarket.com/hc/en-us/articles/360033250174-When-should-I-expect-my-order-to-arrive',
            ),
            getFooterLink(
              'RETURN',
              'https://help.backmarket.com/hc/en-us/categories/360002106194-Returns-refunds-',
            ),
          ],
        },
        {
          title: translations.categoryServices,
          content: [
            getFooterLink('BACKMARKET_WARRANTY'),
            getFooterLink('INSURANCES'),
            getFooterLink('BUY_BACK'),
            getFooterLink('STUDENTS_GOOD_DEALS'),
            getFooterLink('MILITARY_PROGRAM'),
            // getFooterLink('REFER_FRIEND'), // Not live yet
            getFooterLink(
              'BECOME_REFURBISHER',
              'https://backmarket.com/en-us/seller/home',
            ),
            getFooterLink('BO_MERCHANT'),
            getFooterLink('PRO', 'https://pro.backmarket.com'),
            {
              key: translations.paymentMethods,
              type: 'payment-networks-with-link',
              url: 'https://help.backmarket.com/hc/en-us/articles/360026643854-What-payment-methods-are-accepted-on-Back-Market',
            },
          ],
        },
        {
          title: translations.categoryGuides,
          content: [
            getFooterLink('BUYING_GUIDES'),
            getFooterLink('MODELS_COMPARISON'),
            getFooterLink('GIFT_FINDER'),
            getFooterLink('BLACK_FRIDAY'),
          ],
        },
        {
          title: translations.categoryLaw,
          content: [
            getFooterLink('TERMS_SERVICE'),
            getFooterLink('TERMS_BUYBACK'),
            getCookiePolicyLink(),
            getCCPALink(),
            getFooterLink('DATA_PROTECTION'),
            getFooterLink(
              'ILLEGAL_CONTENT',
              'https://help.backmarket.com/hc/en-us/requests/new?ticket_form_id=12226993197084',
            ),
          ],
        },
        {
          content: [
            {
              type: 'logos',
              logos: ['bcorp', 'bbb'],
            },
          ],
        },
      ]
    default:
      return null
  }
}
